import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import {
  compliancePct,
  coveragePct,
} from 'scenes/VisitHistory/utils/compliance';
import { roundedPercentages } from 'scenes/VisitHistory/utils/format';

import { TiendaPerfectaVisitHistoryStop } from 'models/TiendaPerfecta';

dayjs.extend(utc);
dayjs.extend(timezone);

type Stop = TiendaPerfectaVisitHistoryStop;

// Regla transitoria mientras la parada no tenga `tipo_parada` (LOCAL | CD | EVENTO);
// el BFF aplica la misma para observaciones y competencia.
const DISTRIBUTION_CENTER_NAME =
  /^(cd\b|c\.d\.?(\s|$)|centro de distribuci[oó]n|bodega)/i;
const DISTRIBUTOR_CODE_NAME = /^R\d{1,2}$/i;

export const TOP_LIMIT = 5;
export const MAP_DEFAULT_DAYS = 5;

export const isDistributionCenter = (label: string | null | undefined) => {
  const name = (label || '').trim();
  return (
    DISTRIBUTION_CENTER_NAME.test(name) || DISTRIBUTOR_CODE_NAME.test(name)
  );
};

/** Las paradas de locales: CD y bodegas son carga, no implementación. */
export const storeStops = (stops: Stop[]) =>
  stops.filter((stop) => !isDistributionCenter(stop.store.label));

const executionKey = (stop: Stop) => `${stop.route.id}|${stop.plannedDate}`;

const materialUnits = (stops: Stop[]) =>
  stops.reduce(
    (sum, stop) => sum + stop.materials.reduce((u, m) => u + m.units, 0),
    0
  );

const counters = (stops: Stop[]) => {
  const executed = stops.filter((s) => s.outcome === 'executed').length;
  const incidents = stops.filter((s) => s.outcome === 'incident').length;
  return {
    planned: stops.length,
    executed,
    incidents,
    pending: stops.length - executed - incidents,
    compliancePct: compliancePct(executed, stops.length),
  };
};

export interface StoreVisitsKpis {
  planned: number;
  executed: number;
  incidents: number;
  pending: number;
  coveragePct: number;
  compliancePct: number;
  routes: number;
  distributors: number;
}

export const buildKpis = (stops: Stop[]): StoreVisitsKpis => {
  const base = counters(stops);
  return {
    ...base,
    coveragePct: coveragePct(base.executed, base.incidents, base.planned),
    routes: new Set(stops.map(executionKey)).size,
    distributors: new Set(stops.map((s) => s.distributor.id)).size,
  };
};

export type IncidentGroupBy = 'type' | 'distributor';

export interface RankingRow {
  key: string;
  label: string;
  count: number;
  /** Tipo: sobre el total de incidencias. Distribuidor: sobre sus planificados. */
  pct: number;
}

export interface Ranking {
  total: number;
  top: RankingRow[];
  restGroups: number;
  restCount: number;
}

export const buildIncidentRanking = (
  stops: Stop[],
  groupBy: IncidentGroupBy,
  noReasonLabel: string
): Ranking => {
  const plannedBy = new Map<string, number>();
  const groups = new Map<string, { label: string; count: number }>();
  let total = 0;

  stops.forEach((stop) => {
    if (groupBy === 'distributor') {
      const id = stop.distributor.id;
      plannedBy.set(id, (plannedBy.get(id) ?? 0) + 1);
    }
    if (stop.outcome !== 'incident') return;
    const key =
      groupBy === 'distributor'
        ? stop.distributor.id
        : stop.incident?.reason?.trim() || noReasonLabel;
    total += 1;
    const label = groupBy === 'distributor' ? stop.distributor.tradeName : key;
    const group = groups.get(key) ?? { label, count: 0 };
    group.count += 1;
    groups.set(key, group);
  });

  const sorted = [...groups.entries()]
    .map(([key, group]) => ({ key, ...group }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  const rest = sorted.slice(TOP_LIMIT);
  const pctOf = (row: { key: string; count: number }) => {
    const base =
      groupBy === 'distributor' ? plannedBy.get(row.key) ?? 0 : total;
    return base > 0 ? Math.round((row.count / base) * 100) : 0;
  };

  return {
    total,
    top: sorted.slice(0, TOP_LIMIT).map((row) => ({ ...row, pct: pctOf(row) })),
    restGroups: rest.length,
    restCount: rest.reduce((sum, row) => sum + row.count, 0),
  };
};

/** Mismo material con distinto formato ("PENDON" / "Pendon") cuenta una vez. */
export const materialKey = (name: string) =>
  name.trim().replace(/\s+/g, ' ').toUpperCase();

export const properCase = (name: string) => {
  const clean = name.trim().replace(/\s+/g, ' ').toLowerCase();
  return clean.charAt(0).toUpperCase() + clean.slice(1);
};

export interface MaterialRow {
  key: string;
  label: string;
  units: number;
  pct: number;
}

export interface MaterialSummary {
  units: number;
  materials: number;
  perExecuted: number;
  top: MaterialRow[];
  other: { materials: number; units: number; pct: number } | null;
  all: MaterialRow[];
}

export const buildMaterialSummary = (stops: Stop[]): MaterialSummary => {
  const groups = new Map<string, { label: string; units: number }>();
  stops.forEach((stop) =>
    stop.materials.forEach((m) => {
      const key = materialKey(m.name || m.sku || m.materialId);
      const group = groups.get(key) ?? {
        label: properCase(m.name || m.sku || ''),
        units: 0,
      };
      group.units += m.units;
      groups.set(key, group);
    })
  );

  const sorted = [...groups.entries()]
    .map(([key, g]) => ({ key, label: g.label, units: g.units }))
    .filter((row) => row.units > 0)
    .sort((a, b) => b.units - a.units || a.label.localeCompare(b.label));
  const units = sorted.reduce((sum, row) => sum + row.units, 0);
  const top = sorted.slice(0, TOP_LIMIT);
  const rest = sorted.slice(TOP_LIMIT);
  const restUnits = rest.reduce((sum, row) => sum + row.units, 0);
  const shares = roundedPercentages([
    ...top.map((row) => row.units),
    ...(rest.length ? [restUnits] : []),
  ]);
  const executed = stops.filter((s) => s.outcome === 'executed').length;
  const allShares = roundedPercentages(sorted.map((row) => row.units));

  return {
    units,
    materials: sorted.length,
    perExecuted: executed > 0 ? Math.round((units / executed) * 10) / 10 : 0,
    top: top.map((row, index) => ({ ...row, pct: shares[index] })),
    other: rest.length
      ? { materials: rest.length, units: restUnits, pct: shares[top.length] }
      : null,
    all: sorted.map((row, index) => ({ ...row, pct: allShares[index] })),
  };
};

export interface DaySummary {
  date: string;
  routes: number;
  routesWithIncidents: number;
  planned: number;
  executed: number;
  incidents: number;
  pending: number;
  compliancePct: number;
  materialUnits: number;
}

const groupBy = <T>(items: T[], keyOf: (item: T) => string) => {
  const groups = new Map<string, T[]>();
  items.forEach((item) => {
    const key = keyOf(item);
    groups.set(key, [...(groups.get(key) ?? []), item]);
  });
  return groups;
};

/** Una fila por día con rutas, del más reciente al más antiguo. */
export const buildDaySummaries = (stops: Stop[]): DaySummary[] =>
  [...groupBy(stops, (s) => s.plannedDate).entries()]
    .map(([date, list]) => {
      const routes = groupBy(list, executionKey);
      return {
        date,
        routes: routes.size,
        routesWithIncidents: [...routes.values()].filter((r) =>
          r.some((s) => s.outcome === 'incident')
        ).length,
        ...counters(list),
        materialUnits: materialUnits(list),
      };
    })
    .sort((a, b) => b.date.localeCompare(a.date));

export const bestAndWorstDay = (days: DaySummary[]) => {
  const ranked = days
    .filter((d) => d.planned > 0)
    .sort(
      (a, b) =>
        a.compliancePct - b.compliancePct || a.date.localeCompare(b.date)
    );
  return ranked.length
    ? { worst: ranked[0], best: ranked[ranked.length - 1] }
    : null;
};

export interface DayRoute {
  key: string;
  routeId: string;
  routeCode: string;
  date: string;
  zoneId: string;
  zoneName: string;
  distributorName: string;
  start: string | null;
  end: string | null;
  planned: number;
  executed: number;
  incidents: number;
  pending: number;
  compliancePct: number;
  topIncidents: { reason: string; count: number }[];
  /** Comunas de la ruta, la más visitada primero. */
  communes: string[];
}

const clockOf = (iso: string | null, tz: string) =>
  iso ? dayjs(iso).tz(tz).format('HH:mm') : null;

export const buildDayRoutes = (
  stops: Stop[],
  date: string,
  tz: string,
  noReasonLabel: string
): DayRoute[] =>
  [
    ...groupBy(
      stops.filter((s) => s.plannedDate === date),
      executionKey
    ).entries(),
  ]
    .map(([key, list]) => {
      const first = list[0];
      const starts = list
        .map((s) => s.startedAt)
        .filter((v): v is string => !!v)
        .sort();
      const ends = list
        .map((s) => s.finishedAt)
        .filter((v): v is string => !!v)
        .sort();
      const reasons = new Map<string, number>();
      list
        .filter((s) => s.outcome === 'incident')
        .forEach((s) => {
          const reason = s.incident?.reason?.trim() || noReasonLabel;
          reasons.set(reason, (reasons.get(reason) ?? 0) + 1);
        });
      const communes = new Map<string, number>();
      list.forEach((s) => {
        const commune = communeFromAddress(s.store.address);
        if (commune) communes.set(commune, (communes.get(commune) ?? 0) + 1);
      });
      return {
        key,
        routeId: first.route.id,
        routeCode: first.route.code,
        date,
        zoneId: first.zone.id,
        zoneName: first.zone.name,
        distributorName: first.distributor.tradeName,
        start: clockOf(starts[0] ?? null, tz),
        end: clockOf(ends[ends.length - 1] ?? null, tz),
        ...counters(list),
        topIncidents: [...reasons.entries()]
          .map(([reason, count]) => ({ reason, count }))
          .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason))
          .slice(0, 2),
        communes: [...communes.entries()]
          .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
          .map(([name]) => name),
      };
    })
    .sort((a, b) => a.routeCode.localeCompare(b.routeCode));

export interface MapZone {
  id: string;
  name: string;
  routes: number;
}

/** Zonas con rutas en el periodo, la de más rutas primero. */
export const buildMapZones = (stops: Stop[]): MapZone[] =>
  [...groupBy(stops, (s) => s.zone.id).entries()]
    .map(([id, list]) => ({
      id,
      name: list[0].zone.name,
      routes: new Set(list.map(executionKey)).size,
    }))
    .sort((a, b) => b.routes - a.routes || a.name.localeCompare(b.name));

/** Los últimos `limit` días con rutas de la zona, en orden ascendente. */
export const lastRouteDates = (
  stops: Stop[],
  zoneId: string,
  limit = MAP_DEFAULT_DAYS
): string[] =>
  [
    ...new Set(
      stops.filter((s) => s.zone.id === zoneId).map((s) => s.plannedDate)
    ),
  ]
    .sort()
    .slice(-limit);

const REGION_NAMES = [
  'arica y parinacota',
  'tarapaca',
  'antofagasta',
  'atacama',
  'coquimbo',
  'valparaiso',
  'region metropolitana',
  'region metropolitana de santiago',
  'santiago metropolitan region',
  'o higgins',
  'libertador general bernardo o higgins',
  'maule',
  'nuble',
  'bio bio',
  'biobio',
  'araucania',
  'la araucania',
  'los rios',
  'los lagos',
  'aysen',
  'magallanes',
  'magallanes y la antartica chilena',
];

export const normalizePlace = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const REGIONS = new Set(REGION_NAMES);
const POSTAL_CODE = /^\d{5,8}\s+/;

/**
 * Comuna desde la dirección geocodificada: `calle, [código postal] Comuna,
 * Región, Chile`. Transitorio hasta que el local tenga ubigeo; null si la
 * dirección no trae una comuna reconocible.
 */
export const communeFromAddress = (address: string | null | undefined) => {
  const parts = (address || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length && normalizePlace(parts[parts.length - 1]) === 'chile') {
    parts.pop();
  }
  if (
    parts.length >= 3 &&
    REGIONS.has(normalizePlace(parts[parts.length - 1]))
  ) {
    parts.pop();
  }
  if (parts.length < 2) return null;
  const commune = parts[parts.length - 1].replace(POSTAL_CODE, '').trim();
  return commune && !/\d/.test(commune) ? commune : null;
};

export const COMMUNE_TOP = 12;
export const COMMUNE_PIE_TOP = 5;

export interface CommuneRow {
  key: string;
  label: string;
  planned: number;
  executed: number;
  incidents: number;
  pending: number;
  compliancePct: number;
}

export interface CommuneSummary {
  communes: number;
  withoutCommune: number;
  planned: number;
  top: CommuneRow[];
  other: (Omit<CommuneRow, 'key' | 'label'> & { communes: number }) | null;
  /** Torta: top 5 + "otras", con % que suman 100. */
  pie: { key: string; label: string; planned: number; pct: number }[];
}

export const buildCommuneSummary = (stops: Stop[]): CommuneSummary => {
  const groups = new Map<
    string,
    { names: Map<string, number>; stops: Stop[] }
  >();
  let withoutCommune = 0;
  stops.forEach((stop) => {
    const commune = communeFromAddress(stop.store.address);
    if (!commune) {
      withoutCommune += 1;
      return;
    }
    const key = normalizePlace(commune);
    const group = groups.get(key) ?? {
      names: new Map<string, number>(),
      stops: [] as Stop[],
    };
    group.names.set(commune, (group.names.get(commune) ?? 0) + 1);
    group.stops.push(stop);
    groups.set(key, group);
  });

  // La grafía más usada (con tildes) representa al grupo.
  const rows: CommuneRow[] = [...groups.entries()]
    .map(([key, group]) => ({
      key,
      label: [...group.names.entries()].sort(
        (a, b) => b[1] - a[1] || a[0].localeCompare(b[0])
      )[0][0],
      ...counters(group.stops),
    }))
    .sort((a, b) => b.planned - a.planned || a.label.localeCompare(b.label));

  const rest = rows.slice(COMMUNE_TOP);
  const restStops = rest.flatMap((row) => groups.get(row.key)?.stops ?? []);
  const pieRest = rows.slice(COMMUNE_PIE_TOP);
  const pieRows = [
    ...rows.slice(0, COMMUNE_PIE_TOP),
    ...(pieRest.length
      ? [
          {
            key: '__other__',
            label: '',
            planned: pieRest.reduce((sum, row) => sum + row.planned, 0),
          },
        ]
      : []),
  ];
  const shares = roundedPercentages(pieRows.map((row) => row.planned));

  return {
    communes: rows.length,
    withoutCommune,
    planned: rows.reduce((sum, row) => sum + row.planned, 0),
    top: rows.slice(0, COMMUNE_TOP),
    other: rest.length
      ? { communes: rest.length, ...counters(restStops) }
      : null,
    pie: pieRows.map((row, index) => ({
      key: row.key,
      label: row.label,
      planned: row.planned,
      pct: shares[index],
    })),
  };
};
